/**
 * =============================================================
 * AADHAV ACADEMY – STUDENT PERFORMANCE & TOURNAMENT GAMES THUNKS
 * =============================================================
 */

import { createAsyncThunk } from "@reduxjs/toolkit";
import {
  getStudentPerformance,
  getChessOpenings,
  createTournamentPerformance,
  updateTournamentPerformance,
  deleteTournamentPerformance,
  deleteTournamentGame
} from "../../api/admin/studentPerformance.api";

/* ============================================================
   RESPONSE NORMALIZER (PRESERVES SUMMARY & META)
============================================================ */
const normalizeResponse = (res) => {
  let data = [];

  if (Array.isArray(res?.data)) {
    data = res.data;
  } else if (Array.isArray(res?.data?.data)) {
    data = res.data.data;
  } else if (res?.data && typeof res.data === "object") {
    data = [res.data];
  }

  return {
    data,
    summary: res?.summary || { total_tournaments: 0, total_wins: 0, total_points: 0 },
    meta: res?.meta || {
      total: data.length,
      total_pages: 1,
      page: 1,
      limit: 10,
    },
  };
};

/* ================= FETCH ================= */
export const fetchStudentPerformance = createAsyncThunk(
  "admin/studentPerformance/fetch",
  async (
    { studentId = null, tournamentPerformanceId = null, options = {} } = {},
    { rejectWithValue }
  ) => {
    try {
      const res = await getStudentPerformance(studentId, tournamentPerformanceId, options);

      if (!res.success) {
        return rejectWithValue(res.message);
      }

      // If fetching a single tournament performance record directly by ID, return data and meta/summary as-is
      if (tournamentPerformanceId) {
        return {
          data: res.data || null,
          summary: res.summary || {},
          meta: res.meta || {},
        };
      }

      const normalized = normalizeResponse(res);
      return normalized;

    } catch (error) {
      return rejectWithValue(error.message || "Fetch performance failed");
    }
  }
);

/* ================= FETCH CHESS OPENINGS ================= */
export const fetchChessOpeningsThunk = createAsyncThunk(
  "admin/studentPerformance/fetchOpenings",
  async (search = "", { rejectWithValue }) => {
    try {
      const res = await getChessOpenings(search);

      if (!res.success) {
        return rejectWithValue(res.message || "Failed to fetch chess openings");
      }

      return res.data;
    } catch (error) {
      return rejectWithValue(error.message || "Fetch chess openings failed");
    }
  }
);

/* ================= CREATE ================= */
export const createTournamentPerformanceThunk = createAsyncThunk(
  "admin/studentPerformance/create",
  async (payload, { rejectWithValue }) => {
    try {
      const res = await createTournamentPerformance(payload);

      if (!res.success) {
        return rejectWithValue(res.message);
      }

      return res;
    } catch (error) {
      return rejectWithValue(error.message || "Create tournament performance failed");
    }
  }
);

/* ================= UPDATE ================= */
export const updateTournamentPerformanceThunk = createAsyncThunk(
  "admin/studentPerformance/update",
  async ({ id, payload }, { rejectWithValue }) => {
    try {
      if (!id) {
        return rejectWithValue("Tournament Performance ID is required");
      }

      const res = await updateTournamentPerformance(id, payload);

      if (!res.success) {
        return rejectWithValue(res.message);
      }

      return {
        ...res,
        id,
        requestPayload: payload,
      };
    } catch (error) {
      return rejectWithValue(error.message || "Update tournament performance failed");
    }
  }
);

/* ================= DELETE PERFORMANCE ================= */
export const deleteTournamentPerformanceThunk = createAsyncThunk(
  "admin/studentPerformance/deleteTournament",
  async (tournamentPerformanceId, { rejectWithValue }) => {
    try {
      if (!tournamentPerformanceId) {
        return rejectWithValue("Tournament Performance ID is required");
      }

      const res = await deleteTournamentPerformance(tournamentPerformanceId);

      if (!res.success) {
        return rejectWithValue(res.message || "Failed to delete tournament performance");
      }

      return { tournamentPerformanceId };
    } catch (error) {
      return rejectWithValue(error.message || "Delete tournament failed");
    }
  }
);

/* ================= DELETE GAME ROUND ================= */
export const deleteTournamentGameThunk = createAsyncThunk(
  "admin/studentPerformance/deleteGame",
  async ({ gameId, tournamentId }, { rejectWithValue }) => {
    try {
      if (!gameId) {
        return rejectWithValue("Game ID is required");
      }

      const res = await deleteTournamentGame(gameId);

      if (!res.success) {
        return rejectWithValue(res.message || "Failed to delete game record");
      }

      return { gameId, tournamentId };
    } catch (error) {
      return rejectWithValue(error.message || "Delete game failed");
    }
  }
);